import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
    FirmwareApiService,
    GetUpdateProgressResponse,
    UpdateProcess,
    UpdateProcessState,
    RebootStatus,
} from '@kempoweroyj/ngx-firmware-api-client';
import { NotificationsService } from 'api/notifications.api';
import {
    FirmwarePackageComplete,
    GetFirmwareResponse,
    UpdateRegistryApiService,
} from '@kempoweroyj/ngx-update-registry-api-client';
import { combineLatest, firstValueFrom, from, Observable, of, Subject } from 'rxjs';
import {
    auditTime,
    catchError,
    distinctUntilChanged,
    exhaustMap,
    filter,
    first,
    map,
    publishReplay,
    refCount,
    shareReplay,
    startWith,
    switchMap,
} from 'rxjs/operators';
import { parseVersionFromFilename } from 'shared/lib/parseVersionFromFilename';
import { StationsService } from 'shared/stations-service/stations.service';
import * as moment from 'moment';
import { groupBy as _groupBy } from 'lodash';
import { compareSemVersionsWithOperator } from '@kempowersw/csms-utils';
import { AuthService } from 'providers/auth.service';

/**
 * Mapping of Update process states to human readable Firmware status
 */
export const UPDATE_PROCESS_STATE_MAPPING: Record<UpdateProcessState, string> = {
    Scheduled: 'Update scheduled',

    NotStarted: 'Waiting to update',
    Idle: 'Waiting to update',
    Downloading: 'Waiting to update',
    DownloadPaused: 'Waiting to update',

    Started: 'Updating...',
    Installing: 'Updating...',
    InstallRebooting: 'Updating...',

    // Misc
    SignatureVerified: 'Signature verified',
    Downloaded: 'Downloaded',

    // Errors
    DownloadFailed: 'Download failed',
    InstallationFailed: 'Update failed',
    InvalidSignature: 'Invalid signature',
    Cancelled: 'Update cancelled',

    // Success
    Installed: 'Installed', // check if station waits for reboot separately
};
export interface OnGoingUpdate {
    state: UpdateProcessState;
    waitsReboot?: boolean;
    installedFwVersion?: string;
    id?: number;
    rebootRequested?: boolean;
    rebootStatus?: RebootStatus;
}

export interface GroupedByApprovals {
    APPROVED?: FirmwarePackageComplete[];
    TESTING_ALLOWED?: FirmwarePackageComplete[];
    DENIED?: FirmwarePackageComplete[];
    /**
     * Firmware packages that have no approval status (not valid status in API)
     */
    NO_APPROVAL_STATUS?: FirmwarePackageComplete[];
}
export interface LatestFirmwarePackages {
    BETA?: {
        signed?: FirmwarePackageComplete;
        unsigned?: FirmwarePackageComplete;
    };
    DEV?: {
        signed?: FirmwarePackageComplete;
        unsigned?: FirmwarePackageComplete;
    };
    PATCH?: {
        signed?: FirmwarePackageComplete[];
        unsigned?: FirmwarePackageComplete[];
    };
    RELEASE?: {
        signed?: FirmwarePackageComplete;
        unsigned?: FirmwarePackageComplete;
    };
    STABLE?: {
        signed?: FirmwarePackageComplete;
        unsigned?: FirmwarePackageComplete;
    };
}

@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
export class FirmwareService {
    _approvals: GroupedByApprovals;

    private _stationUpdateLogsObservableCache: { [stationId: string]: Observable<GetUpdateProgressResponse> } = {};
    private _stationUpdateLogsRefreshRequest$: { [stationId: string]: Subject<void> } = {};
    private _firmwareResponseCache = new Map<string, Observable<GetFirmwareResponse>>();

    private readonly refreshRequested$ = new Subject<void>();
    private _firmwareResponse$: Observable<GetFirmwareResponse>;

    private getCacheKey(opts?: { currentFwVersion?: string }): string {
        return opts?.currentFwVersion || 'default';
    }

    private getFirmwarePackagesShared$(opts?: { currentFwVersion: string }): Observable<GetFirmwareResponse> {
        const permissions$ = combineLatest([
            this.auth.authenticated$.pipe(
                filter((isAuthenticated) => isAuthenticated),
                distinctUntilChanged()
            ),
            this.auth.hasAccessTo$(['fleet.chargingStation.firmware.access']).pipe(
                filter((hasAccess) => hasAccess),
                distinctUntilChanged()
            ),
        ]);

        return this.refreshRequested$.pipe(
            startWith(null),
            switchMap(() => permissions$),
            map(([isAuthenticated, hasFirmwareAccess]) => isAuthenticated && hasFirmwareAccess),
            filter((v) => !!v),
            switchMap(() => this.requestAllFirmwarePackages$({ ...opts, latest: false })),
            map((res: GetFirmwareResponse) => {
                if (!res) {
                    console.error('No firmwares found');
                    return null;
                }
                return res;
            }),
            filter((v) => !!v),
            shareReplay(1) // Cache the response and share with future subscribers
        );
    }

    constructor(
        private readonly auth: AuthService,
        private readonly updateRegistry: UpdateRegistryApiService,
        private readonly stationsService: StationsService,
        private readonly notifications: NotificationsService,
        private readonly firmwareApi: FirmwareApiService
    ) {
        this.auth.authenticated$.subscribe({
            next: (isAuthenticated) => {
                if (isAuthenticated) {
                    // Refresh when user logs in or impersonates
                    this.refreshRequested$.next();
                }
            },
        });
    }

    firmwarePackages$(opts?: { currentFwVersion: string }): Observable<FirmwarePackageComplete[]> {
        const cacheKey = this.getCacheKey(opts);

        if (!this._firmwareResponseCache.has(cacheKey)) {
            this._firmwareResponseCache.set(cacheKey, this.getFirmwarePackagesShared$(opts));
        }

        return this._firmwareResponseCache.get(cacheKey)!.pipe(map((res) => res.firmwares));
    }

    latestFirmwarePackages$(opts?: { currentFwVersion: string }): Observable<LatestFirmwarePackages> {
        const cacheKey = this.getCacheKey(opts);

        if (!this._firmwareResponseCache.has(cacheKey)) {
            this._firmwareResponseCache.set(cacheKey, this.getFirmwarePackagesShared$(opts));
        }

        return this._firmwareResponseCache.get(cacheKey)!.pipe(map((res) => res.latest));
    }
    getFirmwarePackages(opts?: { currentFwVersion: string }): Promise<FirmwarePackageComplete[]> {
        return firstValueFrom(this.firmwarePackages$(opts));
    }

    getFirmwareApprovals(opts?: { currentFwVersion: string }): Promise<GroupedByApprovals> {
        return firstValueFrom(this.firmwarePackages$(opts).pipe(map((firmwares) => this.groupByApprovals(firmwares))));
    }

    getFirmwarePackage(
        fwVersion: string,
        opts?: { currentFwVersion: string }
    ): Promise<Partial<FirmwarePackageComplete>> {
        return firstValueFrom(
            this.firmwarePackages$(opts).pipe(
                map((firmwares) => {
                    const found: Partial<FirmwarePackageComplete> = [
                        ...firmwares,
                        // local charger setup
                        { versionNumber: 'unknown' },
                    ].find((fw) => fw?.versionNumber === fwVersion);
                    return found;
                })
            )
        );
    }

    getLatestFirmwarePackages(opts?: { currentFwVersion: string }): Promise<LatestFirmwarePackages> {
        return firstValueFrom(this.latestFirmwarePackages$(opts));
    }
    getReleasedFirmwarePackages(opts?: { currentFwVersion: string }): Promise<FirmwarePackageComplete[]> {
        return firstValueFrom(
            this.firmwarePackages$(opts).pipe(
                map((firmwares) => firmwares.filter((fw) => fw.stability === 'STABLE' || fw.stability === 'RELEASE'))
            )
        );
    }

    // Refresh firmwares
    refresh() {
        this.refreshRequested$.next();
        this._firmwareResponse$ = undefined;
        this._firmwareResponseCache.clear();
    }

    requestAllFirmwarePackages$(opts: { latest: boolean; currentFwVersion?: string } = { latest: false }) {
        const queryParams = {
            currentFirmwareVersion: opts.currentFwVersion,
            ...(opts.latest && { latest: opts.latest }),
        };
        return from(this.updateRegistry.getFirmware(queryParams)).pipe(
            catchError((err) => {
                console.error(`updateRegistry#getFirmware ${JSON.stringify(err, null, 2)}`);
                return of(null);
            })
        );
    }

    // overload 1
    isFwVersionFaulty(fwVersion: string): Promise<boolean>;

    // overload 2
    isFwVersionFaulty(fw: Partial<FirmwarePackageComplete>): Promise<boolean>;

    async isFwVersionFaulty(fwLike: string | Partial<FirmwarePackageComplete>) {
        let fwPackage: Partial<FirmwarePackageComplete>;

        if (typeof fwLike === 'string') {
            fwPackage = await this.getFirmwarePackage(fwLike);
        } else {
            fwPackage = fwLike;
        }

        if (!fwPackage) {
            // Firmware version not found in registry. Assume it's OK since it's not explicitly marked bad.
            return false;
        }

        // Found from list - check faulty status
        const isMarked = fwPackage?.stability === 'FAULTY' || fwPackage?.status === 'Blacklisted';
        if (isMarked) {
            return true; // Marked as faulty
        }

        // Not marked as faulty, assume it's OK
        return false;
    }

    private groupByApprovals(fws: FirmwarePackageComplete[]): GroupedByApprovals {
        const grouped = _groupBy(fws || [], 'approvalStatus');

        const approvals = {
            ...(grouped.APPROVED ? { APPROVED: grouped.APPROVED } : {}),
            ...(grouped.TESTING_ALLOWED ? { TESTING_ALLOWED: grouped.TESTING_ALLOWED } : {}),
            ...(grouped.DENIED ? { DENIED: grouped.DENIED } : {}),
            ...(grouped.undefined ? { NO_APPROVAL_STATUS: grouped.undefined } : {}),
        };

        return approvals;
    }

    async getPossibleFirmwaresForStation(stationId: string) {
        const station = await this.stationsService.station$(stationId).pipe(first()).toPromise();
        const currentFwVersion = station.station.firmwareVersion;

        if (
            compareSemVersionsWithOperator({
                currentFw: currentFwVersion,
                compareOperator: '>=',
                compareToFw: '0.12',
            })
        ) {
            // Station has firmware which requires signed firmware
            return (await this.getFirmwarePackages({ currentFwVersion })).filter((e) => e.signed);
        } else {
            // Station has firmware which requires unsigned firmware
            return (await this.getFirmwarePackages({ currentFwVersion })).filter((e) => !e.signed);
        }
    }

    private filterToLatestPatch(firmwareVersion: string, firmwareResults: FirmwarePackageComplete[]) {
        const [major, minor] = firmwareVersion.split('.');
        const patchVersions = firmwareResults.filter(
            (fw) => fw.versionNumber?.startsWith(`${major}.${minor}.`) && fw.versionNumber !== firmwareVersion
        );
        return patchVersions.length > 0 ? patchVersions[0] : ({} as FirmwarePackageComplete);
    }

    async getLatestFirmwaresForStation(currentFwVersion: string) {
        const fws = await this.getLatestFirmwarePackages({ currentFwVersion });
        if (
            compareSemVersionsWithOperator({
                currentFw: currentFwVersion,
                compareOperator: '>=',
                compareToFw: '0.12',
            })
        ) {
            // Station has firmware which requires signed firmware

            return {
                release: { ...fws.RELEASE?.signed },
                stable: { ...fws.STABLE?.signed },
                patch: this.filterToLatestPatch(currentFwVersion, fws.PATCH?.signed),
                beta: { ...fws.BETA?.signed },
                dev: { ...fws.DEV?.signed },
            };
        } else {
            // Station has firmware which requires unsigned firmware
            return {
                release: { ...fws.RELEASE?.unsigned },
                stable: { ...fws.STABLE?.unsigned },
                patch: this.filterToLatestPatch(currentFwVersion, fws.PATCH?.unsigned),
                beta: { ...fws.BETA?.unsigned },
                dev: { ...fws.DEV?.unsigned },
            };
        }
    }

    stationUpdateLogsRefreshRequest$(stationId: string) {
        if (!this._stationUpdateLogsRefreshRequest$[stationId]) {
            this._stationUpdateLogsRefreshRequest$[stationId] = new Subject<void>();
            this.notifications.notification$
                .pipe(
                    filter(
                        (n) =>
                            n.type === 'ChargingStationUpdated' &&
                            n.stationId === stationId &&
                            n.changedSchemas?.includes('updateProcessNotification')
                    )
                )
                .subscribe(() => {
                    this._stationUpdateLogsRefreshRequest$[stationId].next();
                });
            this.notifications.connected$.pipe(filter((connected) => connected)).subscribe(() => {
                this._stationUpdateLogsRefreshRequest$[stationId].next();
            });
        }
        return this._stationUpdateLogsRefreshRequest$[stationId];
    }

    stationUpdateLogs$(stationId: string): Observable<GetUpdateProgressResponse> {
        if (!this._stationUpdateLogsObservableCache[stationId]) {
            this._stationUpdateLogsObservableCache[stationId] = this.stationUpdateLogsRefreshRequest$(stationId).pipe(
                auditTime(500),
                startWith([]),
                exhaustMap(() =>
                    this.firmwareApi.getUpdateProgress(stationId).catch((e) => {
                        console.error('stationUpdateLogs$', e);
                        return Promise.resolve<GetUpdateProgressResponse>(undefined);
                    })
                ),
                publishReplay(1),
                refCount()
            );
        }
        return this._stationUpdateLogsObservableCache[stationId];
    }

    stationOnGoingUpdate$(stationId: string) {
        const station$ = this.stationsService.station$(stationId);
        const updateLogs$ = this.stationUpdateLogs$(stationId).pipe(
            map(
                (e) =>
                    e.updates.sort((a, b) =>
                        Date.parse(a.createdAt) < Date.parse(b.createdAt)
                            ? 1
                            : Date.parse(b.createdAt) < Date.parse(a.createdAt)
                              ? -1
                              : 0
                    )[0]
            )
        );

        return combineLatest([station$, updateLogs$]).pipe(
            map(([station, updateLog]) => {
                const res: OnGoingUpdate = {
                    state: updateLog?.state,
                    waitsReboot: false,
                    id: updateLog?.id,
                    rebootRequested: updateLog?.rebootAfterInstall,
                    rebootStatus: updateLog?.rebootStatus,
                };

                const updateLogFwVersion = parseVersionFromFilename(updateLog?.filename);
                res.installedFwVersion = updateLogFwVersion; // set installedFwVersion

                const stationFwVersion = station.station.firmwareVersion;
                const waitsReboot = this.isWaitForReboot(updateLog, stationFwVersion);

                res.waitsReboot = waitsReboot; // set waitsReboot flag

                return res;
            })
        );
    }

    /**
     * If the station has a different firmware version than the one that recorded in log, we assume the station is waiting for a reboot
     *  !NOTE: Currently we don't know if the charging station has been updated via SSH
     */
    isWaitForReboot(lastLog: UpdateProcess, stationFwVersion: string) {
        if (!lastLog) {
            return false;
        }

        const lastLogFwVersion = parseVersionFromFilename(lastLog.filename);
        const installedEndedAt = lastLog?.installEndedAt;

        const isOverWeekOld = installedEndedAt
            ? Date.parse(installedEndedAt) < moment().subtract(1, 'week').valueOf()
            : true;

        const isWaitForReboot =
            !isOverWeekOld &&
            (lastLog.state === 'Installed' || (lastLog as any)?.state === 'WaitForReboot') &&
            stationFwVersion !== lastLogFwVersion;

        return isWaitForReboot;
    }
}
